import React from "react";
import type { ContentDocument } from "../../API/Models";
import { LoadingSpinner } from "../LoadingSpinner";
import { Modal } from "../Modal";

export type PageEditorModalsProps = {
	// Delete confirmation modal
	showDeleteConfirm: boolean;
	onHideDeleteConfirm: () => void;
	onConfirmDelete: () => void;
	deleting: boolean;

	// Slug change modal
	showSlugChange: boolean;
	onHideSlugChange: () => void;
	slugChangeValue: string;
	onSlugChangeValueChange: (value: string) => void;
	slugChangeError: string | null;
	changingSlug: boolean;
	onConfirmSlugChange: () => void;

	// Dynamic preview selection modal
	showDynamicPreview: boolean;
	onHideDynamicPreview: () => void;
	dynamicPreviewSearch: string;
	onDynamicPreviewSearchChange: (value: string) => void;
	dynamicPreviewError: string | null;
	dynamicPreviewLoading: boolean;
	filteredDynamicPreviewItems: ContentDocument[];
	onSelectDynamicPreviewItem: (item: ContentDocument) => void;
};

/**
 * All modal dialogs used by the PageEditor, extracted as a standalone component.
 * Receives state and handlers as props.
 */
const PageEditorModals: React.FC<PageEditorModalsProps> = ({
	showDeleteConfirm,
	onHideDeleteConfirm,
	onConfirmDelete,
	deleting,
	showSlugChange,
	onHideSlugChange,
	slugChangeValue,
	onSlugChangeValueChange,
	slugChangeError,
	changingSlug,
	onConfirmSlugChange,
	showDynamicPreview,
	onHideDynamicPreview,
	dynamicPreviewSearch,
	onDynamicPreviewSearchChange,
	dynamicPreviewError,
	dynamicPreviewLoading,
	filteredDynamicPreviewItems,
	onSelectDynamicPreviewItem
}) => {
	return (
		<>
			<Modal show={showDeleteConfirm} onHide={onHideDeleteConfirm} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Delete page?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						Are you sure you want to delete this page? This action cannot be undone.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={onHideDeleteConfirm}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={onConfirmDelete}
						className="inline-flex items-center rounded-full border border-rose-200 bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
						disabled={deleting}
					>
						{deleting ? "Deleting\u2026" : "Delete"}
					</button>
				</Modal.Footer>
			</Modal>

			<Modal show={showSlugChange} onHide={onHideSlugChange} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Change slug</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700 mb-3">
						This will create a deleted copy for the old slug and update linked references.
					</p>
					<label className="space-y-1 block">
						<span className="text-xs font-semibold text-slate-500">New slug</span>
						<input
							className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
							value={slugChangeValue}
							onChange={(e) => onSlugChangeValueChange(e.target.value)}
							placeholder="e.g. publications/new-slug"
						/>
					</label>
					{slugChangeError && (
						<p className="mt-2 text-xs text-rose-600">{slugChangeError}</p>
					)}
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={onHideSlugChange}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={onConfirmSlugChange}
						className="inline-flex items-center rounded-full border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
						disabled={changingSlug}
					>
						{changingSlug ? "Updating\u2026" : "Change slug"}
					</button>
				</Modal.Footer>
			</Modal>

			<Modal show={showDynamicPreview} onHide={onHideDynamicPreview} size="lg" centered>
				<Modal.Header closeButton>
					<Modal.Title>Select preview content item</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-600 mb-3">
						Choose a content item to render dynamic variables in the preview. This does not change saved data.
					</p>
					<input
						className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none mb-3"
						placeholder="Search by title, slug, or ID"
						value={dynamicPreviewSearch}
						onChange={(e) => onDynamicPreviewSearchChange(e.target.value)}
					/>
					{dynamicPreviewError && (
						<p className="mb-2 text-xs text-rose-600">{dynamicPreviewError}</p>
					)}
					{dynamicPreviewLoading ? (
						<div className="flex items-center gap-2 text-slate-500 text-sm">
							<LoadingSpinner size="sm" /> Loading items...
						</div>
					) : (
						<div className="max-h-[360px] overflow-auto space-y-2">
							{filteredDynamicPreviewItems.length === 0 ? (
								<p className="text-sm text-slate-500">No content items found.</p>
							) : (
								filteredDynamicPreviewItems.map((item) => {
									const data = (item.data ?? {}) as { title?: string; slug?: string };
									const label = data.title ?? data.slug ?? item.id ?? "Content item";
									return (
										<button
											type="button"
											key={item.id ?? label}
											onClick={() => onSelectDynamicPreviewItem(item)}
											className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 hover:border-slate-300 hover:bg-slate-50"
										>
											<p className="font-semibold text-slate-900">{label}</p>
											<p className="text-xs text-slate-500">{item.id}</p>
										</button>
									);
								})
							)}
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={onHideDynamicPreview}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Close
					</button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default PageEditorModals;
